import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Layers, X, ChevronDown, ChevronRight } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api';
import { inr2, fmtDMY } from '../../lib/format';
import VendorPicker from '../pickers/VendorPicker';

/** #7 — "Club MPRs" suggestions above the MPR list. */
export function ClubMprsPanel({ onCreatePO, onCreateRFQ, openKey, onGroups }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => apiGet('/procurement/mpr-groupings').then(d => { setData(d); onGroups?.(d.groups || []); }).catch(() => setData(null));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  const ref = useRef(null);
  useEffect(() => {
    if (!openKey) return;
    setOpen(openKey.key);
    setTimeout(() => {
      const el = ref.current?.querySelector(`[data-group-key="${window.CSS.escape(openKey.key)}"]`) || ref.current;
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  }, [openKey]);
  if (!data || !data.count) return null;
  const dismiss = async (g) => {
    await apiPost('/procurement/mpr-groupings/dismiss', { group_key: g.group_key }).catch(() => {});
    toast.success('Suggestion hidden for 7 days');
    load();
  };
  return (
    <section ref={ref} className="mb-5 border border-dashed border-indigo-300 bg-indigo-50/30 rounded-sm" data-testid="club-mprs-panel">
      <button className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-indigo-50/60 transition-colors" onClick={() => setOpen(open ? null : '*')}
        aria-expanded={!!open} data-testid="club-mprs-toggle">
        {open ? <ChevronDown size={15} className="text-[#3b4872]" /> : <ChevronRight size={15} className="text-[#3b4872]" />}
        <Layers size={15} className="text-[#3b4872]" />
        <span className="text-sm font-display font-bold text-[#3b4872]">Suggested Club MPRs</span>
        <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-[#3b4872] text-white font-semibold" data-testid="club-mprs-count">{data.count}</span>
        <span className="text-xs text-slate-500 ml-1">Optional · MPRs stay separate unless you choose to club them. Click to {open ? 'hide' : 'view'} suggestions.</span>
      </button>
      {open && (
        <div className="grid md:grid-cols-2 gap-3 p-3 pt-0" data-testid="club-mprs-list">
          {data.groups.map((g, i) => (
            <div key={g.group_key} data-group-key={g.group_key} className={`bg-white border rounded-sm p-3 text-xs ${open === g.group_key ? 'border-[#3b4872] ring-1 ring-[#3b4872]' : 'border-slate-200'}`} data-testid={`club-group-${i}`}>
              <div className="flex justify-between">
                <b className="text-sm">{g.label}</b>
                <button onClick={() => dismiss(g)} className="text-slate-400 hover:text-slate-700" title="Dismiss for 7 days" data-testid={`club-dismiss-${i}`}><X size={13} /></button>
              </div>
              <div className="text-slate-500">{g.kind === 'material' ? 'Same material' : `Category · ${g.category}`}</div>
              <div className="mt-1">Balance: <b>{g.total_balance_qty ?? '—'} {g.uom}</b> · earliest required by {fmtDMY(g.earliest_required_by)}</div>
              <div>MPRs: {g.mprs.join(', ')}</div>
              <div>Projects: {g.projects.join(', ')}</div>
              <div>Indicative value (last PO rate): <b>{inr2(g.indicative_value)}</b></div>
              {g.vendors.length > 0 && <div className="text-slate-500">Vendors: {g.vendors.map(v => v.name).join(', ')}</div>}
              <div className="flex gap-2 mt-2">
                <button onClick={() => onCreateRFQ(g)} className="px-2.5 py-1 border border-[#3b4872] text-[#3b4872] rounded-sm font-semibold" data-testid={`club-rfq-${i}`}>Create RFQ for this group</button>
                <button onClick={() => onCreatePO(g)} className="px-2.5 py-1 bg-[#3b4872] text-white rounded-sm font-semibold" data-testid={`club-po-${i}`}>Create PO for this group</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/** Pick vendors + untick lines, then POST /rfq/from-mprs. */
export function ClubRfqModal({ group, onClose, onDone }) {
  const [lines, setLines] = useState(group.lines.map(l => ({ ...l, on: true, qty: l.balance_qty })));
  const [vlist, setVlist] = useState(group.vendors.filter(v => v.id && v.name));
  const vendors = vlist.map(v => v.id);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await apiPost('/rfq/from-mprs', { vendor_ids: vendors, lines: lines.filter(l => l.on).map(l => ({ mpr_id: l.mpr_id, line_id: l.line_id, qty: Number(l.qty) })) });
      toast.success(`RFQ ${r.rfqs?.[0]?.rfq_no} created`);
      onDone?.();
    } catch (e) { const d = e?.response?.data?.detail; toast.error(typeof d === 'string' ? d : (d?.message || 'Failed')); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" data-testid="club-rfq-modal">
      <div className="bg-white rounded-sm w-full max-w-2xl p-5 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between"><h3 className="font-display font-bold text-[#3b4872]">RFQ for {group.label}</h3><button onClick={onClose}><X size={16} /></button></div>
        <table className="w-full text-xs border border-slate-200"><tbody>{lines.map((l, i) => (
          <tr key={l.line_id} className="border-t border-slate-100">
            <td className="px-2 py-1"><input type="checkbox" checked={l.on} onChange={e => setLines(lines.map((x, j) => (j === i ? { ...x, on: e.target.checked } : x)))} data-testid={`club-rfq-tick-${i}`} /></td>
            <td className="px-2 py-1">{l.material_name}</td><td className="px-2 py-1">{l.mpr_no}</td><td className="px-2 py-1">{l.project_name}</td>
            <td className="px-2 py-1 text-right"><input type="number" value={l.qty} max={l.balance_qty} onChange={e => setLines(lines.map((x, j) => (j === i ? { ...x, qty: Math.min(l.balance_qty, Number(e.target.value)) } : x)))} className="w-20 border border-slate-300 rounded-sm px-1 text-right" /> {l.uom}</td>
          </tr>))}</tbody></table>
        <div><div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Invite vendors</div>
          <div className="flex flex-wrap gap-1 mb-1">{vlist.map(v => <span key={v.id} className="px-2 py-0.5 bg-indigo-50 text-[#3b4872] rounded-full text-xs">{v.name} <button onClick={() => setVlist(vlist.filter(x => x.id !== v.id))}>×</button></span>)}</div>
          <VendorPicker value="" displayName="" onSelect={v => v && !vendors.includes(v.id) && setVlist([...vlist, { id: v.id, name: v.name }])} placeholder="Add vendor…" testid="club-rfq-vendor" /></div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm">Cancel</button>
          <button onClick={submit} disabled={busy || !vendors.length || !lines.some(l => l.on)} className="px-3 py-1.5 bg-[#3b4872] text-white text-sm rounded-sm disabled:opacity-50" data-testid="club-rfq-submit">Create RFQ</button>
        </div>
      </div>
    </div>
  );
}
